import React, { useEffect, useRef } from 'react';
import { useTheme } from '../theme/ThemeContext';
import * as d3 from 'd3';

export default function BiomePieChart({
    multipleSamplesMetaData,
    possibleColorDict,
    scatterplot,
    colors,
    colorDict,
    ids,
    currentPointsSelection,
}) {
    const containerRef = useRef();
    const svgRef = useRef();
    const { theme } = useTheme();

    const getBrightness = (r, g, b) => {
        return (r * 299 + g * 587 + b * 114) / 1000;
    };

    const handleBiomeClick = (biome) => {
        if (!scatterplot || !multipleSamplesMetaData || !ids) return;

        const biomeSamples = multipleSamplesMetaData.filter(sample =>
            sample.meta_data.source_biome === biome
        );

        const biomeIds = biomeSamples.map(sample => sample.run_assembly_id);

        const selectedIndices = biomeIds.map(id => ids.indexOf(id))
            .filter(index => index !== -1);

        if (selectedIndices.length > 0) {
            scatterplot.select(selectedIndices);
        }
    };


    useEffect(() => {
        if (!multipleSamplesMetaData || !possibleColorDict || !containerRef.current) return;

        d3.select(svgRef.current).selectAll('*').remove();

        d3.select(containerRef.current).select('.tooltip').remove();

        const biomeCount = {};
        multipleSamplesMetaData.forEach(sample => {
            const biome = sample.meta_data.source_biome;
            biomeCount[biome] = (biomeCount[biome] || 0) + 1;
        });

        const data = Object.entries(biomeCount)
            .map(([biome, count]) => ({
                biome,
                count
            }))
            .sort((a, b) => b.count - a.count);

        const width = 400;
        const height = 400;
        const radius = Math.min(width, height) / 2;

        const svg = d3.select(svgRef.current)
            .attr('viewBox', `0 0 ${width} ${height}`)
            .append('g')
            .attr('transform', `translate(${width / 2}, ${height / 2})`);

        const pie = d3.pie()
            .value(d => d.count);

        const arc = d3.arc()
            .innerRadius(0)
            .outerRadius(radius - 20);

        const hoverArc = d3.arc()
            .innerRadius(0)
            .outerRadius(radius - 10);

        const tooltip = d3.select(containerRef.current)
            .append('div')
            .attr('class', 'tooltip')
            .style('opacity', 0)
            .style('position', 'absolute')
            .style('background-color', theme.colors.surface)
            .style('color', theme.colors.text)
            .style('padding', '10px')
            .style('border-radius', '5px')
            .style('pointer-events', 'none')
            .style('box-shadow', '0 2px 5px rgba(0,0,0,0.2)')
            .style('font-family', 'Arial, sans-serif')
            .style('font-size', '16px')
            .style('line-height', '1.5')
            .style('z-index', 100);

        let isHovering = false;

        const segments = svg.selectAll('path')
            .data(pie(data))
            .enter()
            .append('path')
            .attr('d', arc)
            .attr('fill', d => {
                const color = possibleColorDict[d.data.biome];
                return color ? `rgb(${color[0]}, ${color[1]}, ${color[2]})` : '#ccc';
            })
            // .attr('stroke', 'white')
            // .attr('stroke', theme.colors.border)
            .attr('stroke', () => {
                const isDarkTheme = theme.colors.background ?
                    getBrightness(...Object.values(d3.rgb(theme.colors.background))) < 128 :
                    theme.mode === 'dark';
                return isDarkTheme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
            })
            .style('stroke-width', '2px')
            .style('cursor', 'pointer')
            .on('mouseover', function (event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('d', hoverArc);

                isHovering = true;

                // const percentage = Math.round((d.data.count / multipleSamplesMetaData.length) * 100);
                const formattedBiome = d.data.biome.replace(/_/g, ' ').replace(/:/g, ' ').replace(/root/g, ' ');

                tooltip
                    .style('opacity', 0.9)
                    .html(`
                        <strong>Biome:</strong> ${formattedBiome}<br/>
                        <strong>Count:</strong> ${d.data.count} samples<br/>
                    `);

                const containerRect = containerRef.current.getBoundingClientRect();
                const [tooltipX, tooltipY] = arc.centroid(d);

                let left = event.pageX - containerRect.left + 15;
                let top = event.pageY - containerRect.top + 10;

                const tooltipNode = tooltip.node();
                if (tooltipNode) {
                    const tooltipRect = tooltipNode.getBoundingClientRect();
                    const maxLeft = containerRect.width - tooltipRect.width;
                    const maxTop = containerRect.height - tooltipRect.height;

                    left = Math.min(left, maxLeft);
                    top = Math.min(Math.max(top, 0), maxTop);
                }

                tooltip
                    .style('left', `${left}px`)
                    .style('top', `${top}px`);
            })
            .on('mousemove', function (event) {
                if (!isHovering) return;

                const containerRect = containerRef.current.getBoundingClientRect();

                let left = event.pageX - containerRect.left + 15;
                let top = event.pageY - containerRect.top + 10;

                const tooltipNode = tooltip.node();
                if (tooltipNode) {
                    const tooltipRect = tooltipNode.getBoundingClientRect();
                    const maxLeft = containerRect.width - tooltipRect.width;
                    const maxTop = containerRect.height - tooltipRect.height;

                    left = Math.min(left, maxLeft);
                    top = Math.min(Math.max(top, 0), maxTop);
                }

                tooltip
                    .style('left', `${left}px`)
                    .style('top', `${top}px`);
            })
            .on('mouseout', function () {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('d', arc);

                isHovering = false;
                setTimeout(() => {
                    if (!isHovering) {
                        tooltip.transition().duration(200).style('opacity', 0);
                    }
                }, 50);
            })
            .on('click', (event, d) => {
                handleBiomeClick(d.data.biome);
            });

        const labelArc = d3.arc()
            .innerRadius(radius - 60)
            .outerRadius(radius - 60);

        svg.selectAll('text')
            .data(pie(data))
            .enter()
            .append('text')
            .attr('transform', d => `translate(${labelArc.centroid(d)})`)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .text(d => {
                const percentage = Math.round((d.data.count / multipleSamplesMetaData.length) * 100);
                return percentage >= 5 ? `${percentage}%` : '';
            })
            .style('font-size', '18px')
            .style('fill', d => {
                const color = possibleColorDict[d.data.biome];
                if (!color) return theme.colors.text;
                const brightness = getBrightness(color[0], color[1], color[2]);
                return brightness > 160 ? 'black' : 'white';
            })
            .style('pointer-events', 'none');

    }, [multipleSamplesMetaData, possibleColorDict, colorDict, theme]);

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
            <svg
                ref={svgRef}
                style={{
                    width: '100%',
                    height: '100%',
                    margin: 'auto',
                    display: 'block',
                }}
            />
        </div>
    );
}